import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { BRAND, CATEGORIES, CITIES } from "@/lib/data";
import Logo from "@/components/shared/Logo";

export default function Footer() {
  return (
    <footer className="bg-[#0B2545] text-white pt-8 md:pt-20 pb-24 md:pb-16" data-testid="site-footer">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12">
          <div className="md:col-span-5">
            <div className="mb-4 md:mb-8">
              <Logo className="text-[26px] md:text-[32px]" tone="white" />
            </div>
            <div className="overline-light mb-2 md:mb-3">Carztohire · Chattanooga, TN</div>
            <h3 className="font-serif text-2xl md:text-5xl leading-tight text-white">
              Compare car rentals across every major US brand.
            </h3>
            <div className="mt-4 md:mt-8 space-y-3 text-sm text-white/80 max-w-md">
              <a href={BRAND.phoneHref} className="flex items-center gap-3 hover:text-white transition-colors" data-testid="footer-phone">
                <Phone className="h-4 w-4 text-white/70" strokeWidth={1.8} /> {BRAND.phone}
              </a>
              <a href={BRAND.emailHref} className="flex items-center gap-3 hover:text-white transition-colors" data-testid="footer-email">
                <Mail className="h-4 w-4 text-white/70" strokeWidth={1.8} /> {BRAND.email}
              </a>
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-white/70 mt-0.5" strokeWidth={1.8} /> {BRAND.address}
              </div>
            </div>
          </div>

          {/* 3-column mobile link grid (unwraps on desktop via md:contents) */}
          <div className="grid grid-cols-3 gap-4 md:contents">
            <div className="md:col-span-3">
              <div className="overline-light mb-3 md:mb-4">Categories</div>
              <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm">
                {CATEGORIES.slice(0, 6).map((c) => (
                  <li key={c.slug}>
                    <Link to={`/categories/${c.slug}`} className="text-white/70 hover:text-white transition-colors">
                      {c.name}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to="/categories" className="text-white underline underline-offset-4">All →</Link>
                </li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <div className="overline-light mb-3 md:mb-4">Cities</div>
              <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm">
                {CITIES.slice(0, 6).map((c) => (
                  <li key={c.slug}>
                    <Link to={`/cities/${c.slug}`} className="text-white/70 hover:text-white transition-colors">
                      {c.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-2">
              <div className="overline-light mb-3 md:mb-4">Company</div>
              <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm">
                <li><Link to="/about" className="text-white/70 hover:text-white">About</Link></li>
                <li><Link to="/contact" className="text-white/70 hover:text-white">Contact</Link></li>
                <li><Link to="/pricing-guide" className="text-white/70 hover:text-white">Pricing Guide</Link></li>
                <li><Link to="/pickup-guide" className="text-white/70 hover:text-white">Pick-up Guide</Link></li>
                <li><Link to="/faq" className="text-white/70 hover:text-white">FAQ</Link></li>
                <li><Link to="/privacy-policy" className="text-white/70 hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/cookie-policy" className="text-white/70 hover:text-white">Cookie Policy</Link></li>
                <li><Link to="/terms-conditions" className="text-white/70 hover:text-white">Terms &amp; Conditions</Link></li>
                <li><Link to="/admin" className="text-white/70 hover:text-white">Admin</Link></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-8 md:mt-16 pt-6 md:pt-8 border-t border-white/15 space-y-4 md:space-y-6">
          <p className="text-xs text-white/60 leading-relaxed" data-testid="footer-disclaimer">
            Carztohire is a brand of TheCityPair LLC bearing Registration No. 001535164 located at 1736 Holly Oak Ln, Chattanooga, TN 37421 is an independent online Car Booking platform not affiliated with or endorsed by Car Rental Company. All services are independently sourced and fulfilled by licensed third-party suppliers under formal agreements. Availability, pricing, and policies are subject to real-time inventory and market conditions, and fare rules or cancellation terms are set by the provider at the time of booking. By using this website, you acknowledge our independent status and agree to our{" "}
            <Link to="/terms-conditions" className="text-white/85 underline underline-offset-2 hover:text-white">
              Terms &amp; Conditions
            </Link>
            . For assistance, contact our customer support team at{" "}
            <a href={BRAND.emailHref} className="text-white/85 underline underline-offset-2 hover:text-white">
              {BRAND.email}
            </a>{" "}
            &nbsp;|&nbsp; call{" "}
            <a href="tel:+16026101396" className="text-white/85 underline underline-offset-2 hover:text-white">
              1-602-610-1396
            </a>
            .
          </p>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-white/55">
            <span data-testid="footer-copyright">
              © Copyright {new Date().getFullYear()} Carztohire. All Rights Reserved.
            </span>
            <span className="overline-light" data-testid="footer-powered-by">
              Powered by The City Pair LLC
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
